import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { ShieldAlert, Plus, Check, X } from 'lucide-react';

const damageTypes = [
  { value: 'broken_shisha', label: 'Broken Shisha ($400)' },
  { value: 'burn_damage', label: 'Burn Damage ($200)' },
  { value: 'other', label: 'Other (Custom Amount)' },
];

export default function DamageTab() {
  const [claims, setClaims] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState({ customer_id: '', table_id: '', damage_type: 'broken_shisha', description: '', amount: 0 });

  const fetchClaims = useCallback(async () => {
    try { const { data } = await api.get('/damages'); setClaims(data); } catch { /* */ }
  }, []);

  useEffect(() => { fetchClaims(); }, [fetchClaims]);

  const createClaim = async () => {
    try {
      await api.post('/damages/claim', form);
      toast.success('Damage claim created');
      setFormOpen(false);
      setForm({ customer_id: '', table_id: '', damage_type: 'broken_shisha', description: '', amount: 0 });
      fetchClaims();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const approveClaim = async (id) => {
    try { await api.put(`/damages/${id}/approve`); toast.success('Claim approved'); fetchClaims(); } catch (err) { toast.error('Failed'); }
  };

  const rejectClaim = async (id) => {
    try { await api.put(`/damages/${id}/reject`); toast.success('Claim rejected'); fetchClaims(); } catch (err) { toast.error('Failed'); }
  };

  const statusColors = { pending: 'bg-amber-500/20 text-amber-400', approved: 'bg-emerald-500/20 text-emerald-400', rejected: 'bg-red-500/20 text-red-400' };
  const typeLabels = { broken_shisha: 'Broken Shisha', burn_damage: 'Burn Damage', other: 'Other' };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-sm font-sans text-zinc-400 uppercase tracking-wider">{claims.length} claims</h3>
        <Dialog open={formOpen} onOpenChange={setFormOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="bg-gold text-black rounded-sm font-sans text-xs font-bold" data-testid="new-damage-claim-button"><Plus className="w-3 h-3 mr-1" /> New Claim</Button>
          </DialogTrigger>
          <DialogContent className="bg-bg-secondary border border-white/10 max-w-md">
            <DialogHeader><DialogTitle className="font-serif text-white">Report Damage</DialogTitle></DialogHeader>
            <div className="space-y-3 pt-2">
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Damage Type</Label>
                <Select value={form.damage_type} onValueChange={v => setForm(p => ({ ...p, damage_type: v }))}>
                  <SelectTrigger className="bg-transparent border border-white/10 text-white font-sans" data-testid="damage-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-bg-secondary border border-white/10">
                    {damageTypes.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Customer ID</Label>
                  <Input value={form.customer_id} onChange={e => setForm(p => ({ ...p, customer_id: e.target.value }))} className="bg-transparent border border-white/10 text-white font-sans" placeholder="Customer ID" data-testid="damage-customer-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Table ID</Label>
                  <Input value={form.table_id} onChange={e => setForm(p => ({ ...p, table_id: e.target.value }))} className="bg-transparent border border-white/10 text-white font-sans" placeholder="Table ID" data-testid="damage-table-input" />
                </div>
              </div>
              {form.damage_type === 'other' && (
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Amount ($)</Label>
                  <Input type="number" step="0.01" value={form.amount} onChange={e => setForm(p => ({ ...p, amount: parseFloat(e.target.value) || 0 }))} className="bg-transparent border border-white/10 text-white font-sans" data-testid="damage-amount-input" />
                </div>
              )}
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Description / Proof</Label>
                <Input value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} className="bg-transparent border border-white/10 text-white font-sans" placeholder="Describe damage, evidence" data-testid="damage-description-input" />
              </div>
              <Button onClick={createClaim} className="w-full bg-gold text-black font-bold rounded-sm font-sans" data-testid="submit-damage-button">Submit Claim</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-2">
        {claims.map(c => (
          <div key={c.id} className="p-4 bg-white/[0.03] border border-white/5 rounded-sm" data-testid={`damage-claim-${c.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <ShieldAlert className={`w-5 h-5 flex-shrink-0 mt-0.5 ${c.status === 'pending' ? 'text-amber-400' : c.status === 'approved' ? 'text-emerald-400' : 'text-red-400'}`} />
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-white font-sans font-medium">{typeLabels[c.damage_type] || c.damage_type}</p>
                    <Badge className={`${statusColors[c.status]} border-0 font-sans text-xs`}>{c.status}</Badge>
                  </div>
                  <p className="text-gold font-mono font-semibold">${c.amount?.toFixed(2)}</p>
                  {c.description && <p className="text-zinc-400 text-xs font-sans mt-1">{c.description}</p>}
                  <p className="text-zinc-600 text-xs font-sans mt-1">Reported by {c.reporter_name} - {new Date(c.created_at).toLocaleString()}</p>
                </div>
              </div>
              {c.status === 'pending' && (
                <div className="flex gap-1">
                  <Button size="sm" onClick={() => approveClaim(c.id)} className="h-7 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20 rounded-sm" data-testid={`approve-damage-${c.id}`}><Check className="w-3 h-3" /></Button>
                  <Button size="sm" onClick={() => rejectClaim(c.id)} className="h-7 bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 rounded-sm" data-testid={`reject-damage-${c.id}`}><X className="w-3 h-3" /></Button>
                </div>
              )}
            </div>
          </div>
        ))}
        {claims.length === 0 && <p className="text-zinc-500 font-sans text-center py-8" data-testid="no-damage-claims">No damage claims</p>}
      </div>
    </div>
  );
}
